import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";
import EmptyState from "@/components/base/EmptyState";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const item = newsList.find((n) => n.id === id);

  usePageMeta({
    title: item ? `${item.title} - 电影天堂资讯` : "资讯未找到 - 电影天堂",
    description: item
      ? `${item.summary.slice(0, 80)} - 电影天堂影视资讯，带你了解最新影视动态。`
      : "未找到该资讯内容，请返回资讯列表浏览其他文章。",
    keywords: item ? `${item.tags.join(",")},影视资讯,电影天堂` : "影视资讯,电影天堂",
  });

  const related = useMemo(() => {
    if (!item) return [];
    return newsList
      .filter((n) => n.id !== item.id && n.tags.some((t) => item.tags.includes(t)))
      .slice(0, 4);
  }, [item]);

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-emotion-sad-line"
          title="没有找到这篇资讯"
          description="文章可能已被移除或链接有误，你可以返回资讯列表继续浏览。"
          actionLabel="返回资讯列表"
          actionTo="/news"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[820px] px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-sm text-foreground-600">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="transition-colors hover:text-primary-600">影视资讯</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="line-clamp-1 text-foreground-900">正文</span>
      </nav>

      <article>
        <div className="flex flex-wrap gap-2">
          {item.tags.map((t) => (
            <span
              key={t}
              className="rounded-md bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-800"
            >
              {t}
            </span>
          ))}
        </div>
        <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
          {item.title}
        </h1>
        <div className="mt-4 flex items-center gap-4 border-b border-background-200 pb-5 text-sm text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line"></i>
            {item.publishDate}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line"></i>
            电影天堂编辑部
          </span>
        </div>

        <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-200">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} 资讯配图`}
            className="w-full object-top object-cover"
          />
        </div>

        <div className="mt-6 space-y-5">
          {item.content.map((p, i) => (
            <p key={i} className="text-sm leading-8 text-foreground-700 md:text-base">
              {p}
            </p>
          ))}
        </div>

        <p className="mt-8 rounded-lg bg-secondary-50 p-4 text-xs leading-relaxed text-foreground-600">
          <i className="ri-information-line mr-1"></i>
          本站资讯内容均为演示用虚构样本，仅用于展示产品功能，不代表任何真实报道。
        </p>
      </article>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
        >
          <i className="ri-home-4-line"></i>
          回到首页
        </Link>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">相关文章</h2>
          <div className="flex flex-col gap-3">
            {related.map((n) => (
              <Link
                key={n.id}
                to={`/news/detail/${n.id}`}
                className="group flex items-start justify-between gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
              >
                <div>
                  <h3 className="line-clamp-1 text-sm font-medium text-foreground-900 transition-colors group-hover:text-primary-600">
                    {n.title}
                  </h3>
                  <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{n.summary}</p>
                </div>
                <span className="shrink-0 text-xs text-foreground-500">{n.publishDate}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}